import { useCallback, useState } from "react";

import type { ShareStatus } from "@shared/ui/social-shared/social-shared";

import type { Chat } from "./types";

export const useShareLink = (answer: Chat, isProgram?: boolean) => {
  const [shareUrl, setShareUrl] = useState<string | null>(() =>
    answer?.documentId
      ? `${process.env.NEXT_PUBLIC_SITE_URL}/ai-assistant?share=${answer.documentId}`
      : null,
  );

  const [status, setStatus] = useState<ShareStatus>("idle");

  const createShareUrl = useCallback(async () => {
    if (status === "creating") {
      return null;
    }

    try {
      setStatus("creating");

      const payload = {
        question: JSON.stringify(answer.question),
        answer: answer.text || "",
        timestamp: answer.timestamp,
        accommodation: JSON.stringify(answer.accommodation),
        schedule: JSON.stringify(answer.schedule || []),
        key: answer.key,
        dialogue_id: answer.dialogue_id,
        query_response: answer.query_response,
      };

      const res = await fetch("/api/share/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let data: any;
      try {
        data = await res.json();
      } catch (e) {
        setStatus("error");
        return null;
      }

      if (!data.shareUrl) {
        setStatus("error");
        return null;
      }

      setShareUrl(data.shareUrl);
      setStatus("ready");

      return data.shareUrl;
    } catch (e) {
      setStatus("error");
      return null;
    }
  }, [answer, status]);

  const copyShareUrl = useCallback(async () => {
    const url = shareUrl ?? (await createShareUrl());

    if (!url) {
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      setStatus("copied");

      setTimeout(() => {
        setStatus("ready");
      }, 2000);
    } catch (e) {
      setStatus("error");
    }
  }, [shareUrl, createShareUrl]);

  return {
    shareUrl,
    status,
    createShareUrl,
    copyShareUrl,
  };
};
